import React from 'react'
import { Link } from 'react-router-dom'
import { getResizedImageUrl } from '../lib/cms'

export default function VehicleCard({ vehicle }) {
  if (!vehicle) return null

  const imageUrl = Array.isArray(vehicle.image) && vehicle.image.length > 0
    ? vehicle.image[0]
    : null

  const contactUrl = `/kontakt?koeretoej=${encodeURIComponent(vehicle.name)}`

  return (
    <div className="flex flex-col bg-white rounded-2xl border border-slate-200/90 overflow-hidden shadow-xs hover:shadow-md transition-shadow">
      {/* Vehicle image with badge */}
      <div className="relative aspect-16/10 w-full bg-slate-100 overflow-hidden border-b border-slate-150">
        {imageUrl ? (
          <img
            src={getResizedImageUrl(imageUrl, { width: 700, quality: 80 })}
            alt={vehicle.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <i className="fa-solid fa-truck text-4xl text-slate-300" aria-hidden="true" />
          </div>
        )}

        {/* Temperature Badge */}
        {vehicle.temp_range && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-navy-900/90 text-sky-400 text-xs font-bold backdrop-blur-xs border border-sky-400/20 shadow-sm">
            <i className="fa-solid fa-temperature-arrow-down" aria-hidden="true" />
            <span>{vehicle.temp_range}</span>
          </div>
        )}

        {/* Category Badge */}
        {vehicle.category && (
          <div className="absolute top-3 right-3 inline-flex items-center px-2.5 py-1 rounded-lg bg-white/90 text-slate-800 text-[11px] font-semibold backdrop-blur-xs border border-slate-200 shadow-sm">
            {vehicle.category}
          </div>
        )}
      </div>

      {/* Vehicle details */}
      <div className="p-6 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-xl font-bold text-slate-900 tracking-tight">
            {vehicle.name}
          </h3>

          {/* Technical specifications */}
          <div className="mt-5 bg-slate-50/90 rounded-xl border border-slate-200/70 divide-y divide-slate-200/60 overflow-hidden">
            {/* Pallet capacity */}
            <div className="p-3 sm:px-4 flex flex-col gap-0.5">
              <span className="text-[11px] uppercase tracking-wider font-medium text-slate-500">
                Pallekapacitet
              </span>
              <span className="font-bold text-slate-900 text-xs sm:text-sm">
                {vehicle.pallet_capacity || 'Op til 8 europaller'}
              </span>
            </div>

            {/* Payload / Godsstørrelse */}
            <div className="p-3 sm:px-4 flex flex-col gap-0.5">
              <span className="text-[11px] uppercase tracking-wider font-medium text-slate-500">
                Forsendelse
              </span>
              <span className="font-bold text-slate-900 text-xs sm:text-sm break-words">
                {vehicle.payload || 'Fra 1 kg (ikke-standard gods efter aftale)'}
              </span>
            </div>

            {/* Temperaturområde */}
            <div className="p-3 sm:px-4 flex flex-col gap-0.5">
              <span className="text-[11px] uppercase tracking-wider font-medium text-slate-500">
                Temperatur
              </span>
              <span className="font-bold text-sky-700 text-xs sm:text-sm">
                {vehicle.temp_range || '-20°C til +25°C'}
              </span>
            </div>

            {/* Forsikring & Udstyr */}
            <div className="p-3 sm:px-4 flex flex-col gap-0.5">
              <span className="text-[11px] uppercase tracking-wider font-medium text-slate-500">
                Sikkerhed
              </span>
              <span className="font-bold text-slate-900 text-xs sm:text-sm">
                Godsforsikret
              </span>
            </div>
          </div>

          {/* Features bullet summary */}
          {vehicle.features && (
            <div className="mt-4 pt-3 text-xs text-slate-600 flex items-start gap-2">
              <i className="fa-solid fa-check text-sky-600 mt-0.5" aria-hidden="true" />
              <span>{vehicle.features}</span>
            </div>
          )}

          {/* Description */}
          {vehicle.description && (
            <div
              className="mt-3 text-xs text-slate-600 line-clamp-3 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: vehicle.description }}
            />
          )}
        </div>

        {/* Action buttons stacked one under another */}
        <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col gap-2.5">
          <Link
            to={contactUrl}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold transition-colors shadow-xs"
          >
            <span>Forespørg på denne bil</span>
            <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
          </Link>

          <a
            href="tel:+4550352035"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-colors"
            title="Ring 50 35 20 35"
            aria-label="Ring 50 35 20 35"
          >
            <i className="fa-solid fa-phone text-sky-600 text-xs" aria-hidden="true" />
            <span>Ring 50 35 20 35</span>
          </a>
        </div>
      </div>
    </div>
  )
}
